import { Ellipsis } from 'lucide-react'
import type { ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import { useUi } from '../store/ui'
import type { BottomBar as BottomBarModel, NavGroup } from './nav'

const ITEM =
  'relative flex flex-auto flex-col items-center justify-center gap-0.5 px-1 text-label text-muted press focus-settle-inset aria-[current=page]:font-medium aria-[current=page]:text-text aria-[current=page]:before:absolute aria-[current=page]:before:top-0 aria-[current=page]:before:h-0.5 aria-[current=page]:before:w-6 aria-[current=page]:before:bg-primary'

const TICK = 'before:absolute before:top-0 before:h-0.5 before:w-6 before:bg-primary'

function inGroups(groups: readonly NavGroup[], pathname: string): boolean {
  return groups.some((group) =>
    group.items.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`)),
  )
}

interface BottomBarProps {
  bar: BottomBarModel
  /** Plan 10: what heads Lainnya (an admin keuskupan's "Bekerja di"). */
  atas?: ReactNode
}

/**
 * The phone bar: five destinations, hairline above, safe-area padding, labels always
 * visible, the active one with a 2 px red tick on its top edge. "Lainnya" opens a bottom
 * sheet with the remaining groups in shelf order. It always ends with Akun (and Pengaturan
 * for admin paroki), plan 9b; an admin keuskupan's "Bekerja di" heads it (plan 10, `atas`).
 */
export function BottomBar({ bar, atas }: BottomBarProps) {
  const open = useUi((s) => s.lainnyaOpen)
  const setOpen = useUi((s) => s.setLainnyaOpen)
  const { pathname } = useLocation()
  const lainnyaAktif = inGroups(bar.lainnya, pathname)

  return (
    <>
      <nav
        aria-label="Navigasi utama"
        className="vt-bottom fixed inset-x-0 bottom-0 z-20 box-content flex h-14 border-t border-border bg-bg pb-[env(safe-area-inset-bottom)] md:hidden"
      >
        {bar.items.map((item) => {
          const Icon = item.icon
          return (
            <NavLink key={item.to} to={item.to} end={item.end ?? false} className={ITEM}>
              <Icon size={20} strokeWidth={1.75} aria-hidden="true" />
              <span className="whitespace-nowrap">{item.label}</span>
            </NavLink>
          )
        })}
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={open}
          className={cn(ITEM, lainnyaAktif && cn('font-medium text-text', TICK))}
        >
          <Ellipsis size={20} strokeWidth={1.75} aria-hidden="true" />
          <span>Lainnya</span>
        </button>
      </nav>
      <LainnyaSheet
        groups={bar.lainnya}
        open={open}
        onOpenChange={setOpen}
        {...(atas === undefined ? {} : { atas })}
      />
    </>
  )
}

interface LainnyaSheetProps {
  groups: readonly NavGroup[]
  open: boolean
  onOpenChange(open: boolean): void
  /** Plan 10: what heads Lainnya (an admin keuskupan's "Bekerja di"). */
  atas?: ReactNode
}

function LainnyaSheet({ groups, open, onOpenChange, atas }: LainnyaSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="max-h-[80dvh] gap-0 overflow-y-auto pb-[env(safe-area-inset-bottom)]"
      >
        <SheetHeader>
          <SheetTitle>Lainnya</SheetTitle>
        </SheetHeader>
        {atas === undefined ? null : <div className="border-b border-border pb-2">{atas}</div>}
        <nav aria-label="Navigasi lainnya" className="pb-4">
          {groups.map((group, index) => {
            const id = `lainnya-${index}`
            return (
              <div
                key={group.label ?? `tanpa-judul-${index}`}
                // The last group (Akun, Pengaturan) has no heading: a hairline sets it apart.
                className={cn(
                  group.label === null && index > 0 && 'mt-2 border-t border-border pt-2',
                )}
              >
                {group.label !== null ? (
                  <p id={id} className="px-4 pt-2 pb-1 text-label font-medium text-muted">
                    {group.label}
                  </p>
                ) : null}
                <ul aria-labelledby={group.label !== null ? id : undefined}>
                  {group.items.map((item) => {
                    const Icon = item.icon
                    return (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          end={item.end ?? false}
                          onClick={() => onOpenChange(false)}
                          className="relative flex h-12 items-center gap-3 px-4 text-body text-text hover-band press focus-settle-inset aria-[current=page]:font-medium aria-[current=page]:before:absolute aria-[current=page]:before:top-2 aria-[current=page]:before:bottom-2 aria-[current=page]:before:left-0 aria-[current=page]:before:w-0.5 aria-[current=page]:before:bg-primary"
                        >
                          <Icon size={20} strokeWidth={1.75} aria-hidden="true" />
                          <span>{item.label}</span>
                        </NavLink>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )
          })}
        </nav>
      </SheetContent>
    </Sheet>
  )
}
